import { useState } from "react";
import { Link } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, apiErrorMessage } from "@/lib/api";
import { useTranslation } from "@/lib/i18n";

import { getTenantConfig } from "@/lib/tenant";

export function Login() {
  const tenant = getTenantConfig();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  return (
    <main className="min-h-dvh flex items-center justify-center bg-surface-canvas px-4 py-12 sm:px-8">
      <div className="flex w-full max-w-[420px] flex-col items-center gap-6">
        <Link
          to="/"
          className="flex items-center gap-2.5 text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
        >
          {tenant.brandName === "MindfulMe" ? (
            <span className="flex items-center gap-2 font-black text-slate-900 tracking-tight text-2xl sm:text-3xl">
              <span className="relative flex size-8 items-center justify-center">
                <span className="absolute left-0 size-6 rounded-full bg-indigo-600" />
                <span className="absolute right-0 size-6 rounded-full bg-orange-500 opacity-90 mix-blend-multiply" />
              </span>
              <span>Mindful<span className="text-indigo-600">Me</span></span>
            </span>
          ) : (
            <span className="text-primary font-bold">{tenant.brandName}</span>
          )}
        </Link>

        <Card className="w-full shadow-elev-2">
          <CardHeader className="text-center">
            <CardTitle>{t("login_title").replace(/Kidology/g, tenant.brandName)}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-base text-muted-foreground text-center mb-2">
              {t("login_desc").replace(/Kidology/g, tenant.brandName)}
            </p>


            <div className="pt-1">
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                {t("login_no_link")}
                <br />
                {t("login_enter_email")}
              </p>

              {status === "success" ? (
                <div className="rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-500 border border-emerald-500/20 text-center">
                  {t("login_success")}
                </div>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!email.trim()) return;
                    setStatus("loading");
                    setErrorMessage("");
                    try {
                      await api("/api/auth/request-magic-link", {
                        method: "POST",
                        body: JSON.stringify({ email: email.trim() }),
                      });
                      setStatus("success");
                    } catch (err) {
                      setStatus("error");
                      const msg = apiErrorMessage(err, "Failed to send link.");
                      if (msg === "user_not_found") {
                        setErrorMessage(t("login_error_user_not_found"));
                      } else if (msg === "token_not_found") {
                        setErrorMessage(t("login_error_token_not_found"));
                      } else {
                        setErrorMessage(msg);
                      }
                    }
                  }}
                  className="space-y-4"
                >
                  <div className="space-y-2 text-left">
                    <label htmlFor="login-email" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-3">
                      {t("login_email_label")}
                    </label>
                    <input
                      id="login-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("login_placeholder")}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={status === "loading"}
                    />
                  </div>

                  {status === "error" && (
                    <p className="text-sm text-destructive text-center">{errorMessage}</p>
                  )}

                  <Button
                    type="submit"
                    variant="default"
                    size="touch"
                    disabled={status === "loading"}
                    className="w-full"
                  >
                    {status === "loading" ? t("login_sending") : t("login_send_btn")}
                  </Button>
                </form>

              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

